
import { Ref, ref } from "vue";
import { Component, Vue, Prop } from "vue-property-decorator";

import admission_routes from "@/api/routes/admission";

import CInfiniteScroll from "@/components/InfiniteScroll.vue";

import { AdmissionRole } from "@/models/admission/role";
import { IAdmissionPosition } from "@/models/admission/position";
import StructureFactory from "@/models/structure/factory";

interface IAdmissionPositionOption extends IAdmissionPosition {
  is_check: boolean;
}

@Component({
  name: "CAdmissionRoleChangePositions",
  components: {
    CInfiniteScroll
  }
})
export default class CAdmissionRoleChangePositions extends Vue {
  @Prop({ required: true }) public role!: AdmissionRole;
  @Prop({ required: true }) public factories!: StructureFactory[];

  public positions_key: number = 0; // для ререндера
  public positions_key_available: number = 0; // для ререндера
  public positions_key_exists: number = 0; // для ререндера
  public search: string = "";

  public show_filter: boolean = false;
  public filters_count: number = 0;
  public filters: Map<string, boolean> = new Map();

  public factory_options: StructureFactory[] = [
    { id: null, full_name: "Все" },
    ...this.factories
  ];
  public selected_factory: StructureFactory = this.factory_options[0];

  private available_preload_infinite: boolean = false;
  private available_per_page: number = 100;
  private available_current_page: number = 0;
  private available_next_page: boolean = false;
  public available_positions: Ref<Map<string, IAdmissionPositionOption>> = ref(
    new Map()
  );
  public available_positions_key: number = 0; // для ререндера
  public available_positions_select_all: boolean = false;
  public available_positions_checked_count: number = 0;

  private exists_preload_infinite: boolean = false;
  private exists_per_page: number = 100;
  private exists_current_page: number = 0;
  private exists_next_page: boolean = false;
  public exists_positions: Ref<Map<string, IAdmissionPositionOption>> = ref(
    new Map()
  );
  public exists_positions_key: number = 0; // для ререндера
  public exists_positions_select_all: boolean = false;
  public exists_positions_checked_count: number = 0;

  private ids_to_add: Ref<Set<string>> = ref(new Set<string>());
  private ids_to_del: Ref<Set<string>> = ref(new Set<string>());

  protected async created() {
    this.available_positions.value = new Map();
    this.exists_positions.value = new Map();

    this.ids_to_add.value = new Set();
    this.ids_to_del.value = new Set();

    const promises = [this.loadAvailablePositions()];

    if (this.role.id) {
      promises.push(this.loadExistsPositions());
    }

    await Promise.all(promises);

    this.positions_key++;
  }

  public async changeFactoryFilter() {
    if (!this.selected_factory) {
      this.selected_factory = this.factory_options[0];
    }

    if (this.selected_factory.id) {
      this.filters.set("factory", true);
    } else {
      this.filters.delete("factory");
    }

    this.filters_count = this.filters.size;

    await this.apply();
  }

  public async clearFilters() {
    this.filters.clear();

    this.selected_factory = this.factory_options[0];

    this.filters_count = this.filters.size;

    await this.apply();
  }

  public async searchPositions() {
    await this.apply();
  }

  private async apply() {
    this.available_current_page = 0;
    this.available_next_page = false;
    this.available_positions_select_all = false;
    this.available_positions.value = new Map();

    await this.loadAvailablePositions();

    this.positions_key_available++;
  }

  public async availableInfiniteHandler() {
    if (this.available_next_page && !this.available_preload_infinite) {
      await this.loadAvailablePositions(this.available_current_page + 1);
    }
  }

  public async existsInfiniteHandler() {
    if (this.exists_next_page && !this.exists_preload_infinite) {
      await this.loadExistsPositions(this.exists_current_page + 1);
    }
  }

  private async loadAvailablePositions(page: number = 0) {
    this.available_preload_infinite = true;

    return this.$api
      .get(admission_routes.positions_available(this.role.id), {
        params: {
          page,
          per_page: this.available_per_page,
          search: this.search,
          factory_id: this.selected_factory?.id || ""
        }
      })
      .then(({ data: res }: { data: IAdmissionPosition[] }) => {
        this.available_current_page = page;
        this.available_next_page = res.length === this.available_per_page;

        res.forEach(position => {
          if (!this.exists_positions.value.has(position.id)) {
            this.available_positions.value.set(position.id, {
              ...position,
              is_check: this.available_positions_select_all
            });
          }
        });
      })
      .finally(() => {
        this.available_preload_infinite = false;
        this.available_positions_key++;
      });
  }

  private async loadExistsPositions(page: number = 0) {
    this.exists_preload_infinite = true;

    return this.$api
      .get(admission_routes.positions_exists(this.role.id), {
        params: {
          page,
          per_page: this.exists_per_page
        }
      })
      .then(({ data: res }: { data: IAdmissionPosition[] }) => {
        this.exists_current_page = page;
        this.exists_next_page = res.length === this.exists_per_page;

        res.forEach(position => {
          this.exists_positions.value.set(position.id, {
            ...position,
            is_check: this.exists_positions_select_all
          });
        });
      })
      .finally(() => {
        this.exists_preload_infinite = false;
        this.exists_positions_key++;
      });
  }

  public checkAllAvailablePositions() {
    if (this.available_positions_select_all) {
      this.available_positions.value.forEach(position => {
        position.is_check = false;
      });

      this.available_positions_checked_count = 0;
      this.available_positions_select_all = false;
    } else {
      this.available_positions.value.forEach(position => {
        position.is_check = true;
      });

      this.available_positions_checked_count = this.available_positions.value.size;
      this.available_positions_select_all = true;
    }
  }

  public checkAllIconAvailablePositions() {
    return this.available_positions_checked_count === 0
      ? null
      : this.available_positions_select_all
      ? "/img/icon-plus.svg"
      : "/img/icon-minus.svg";
  }

  public async addAllCheckedAvailablePositions() {
    this.available_positions.value.forEach(position => {
      if (position.is_check) {
        position.is_check = false;
        this.available_positions.value.delete(position.id);
        this.exists_positions.value.set(position.id, position);

        this.ids_to_del.value.delete(position.id);
        this.ids_to_add.value.add(position.id);
      }
    });

    this.available_positions_checked_count = 0;
    this.available_positions_select_all = false;
    this.exists_positions_select_all = false;

    if (!this.available_positions.value.size && this.available_next_page) {
      await this.loadAvailablePositions(this.available_current_page + 1);

      this.positions_key_available++;
    }

    this.$emit("change", {
      add: this.ids_to_add.value,
      del: this.ids_to_del.value
    });
  }

  public checkAvailablePosition(position: IAdmissionPositionOption) {
    this.$set(position, "is_check", !position.is_check);

    this.available_positions_checked_count += position.is_check ? 1 : -1;
    this.available_positions_select_all =
      this.available_positions_checked_count ===
      this.available_positions.value.size;
  }

  public checkAllExistsPositions() {
    if (this.exists_positions_select_all) {
      this.exists_positions.value.forEach(position => {
        position.is_check = false;
      });

      this.exists_positions_checked_count = 0;
      this.exists_positions_select_all = false;
    } else {
      this.exists_positions.value.forEach(position => {
        position.is_check = true;
      });

      this.exists_positions_checked_count = this.exists_positions.value.size;
      this.exists_positions_select_all = true;
    }
  }

  public checkAllIconExistsPositions() {
    return this.exists_positions_checked_count === 0
      ? null
      : this.exists_positions_select_all
      ? "/img/icon-plus.svg"
      : "/img/icon-minus.svg";
  }

  public async removeAllCheckedExistsPositions() {
    this.exists_positions.value.forEach(position => {
      if (position.is_check) {
        position.is_check = false;
        this.exists_positions.value.delete(position.id);
        this.available_positions.value.set(position.id, position);

        this.ids_to_add.value.delete(position.id);
        this.ids_to_del.value.add(position.id);
      }
    });

    this.exists_positions_checked_count = 0;
    this.available_positions_select_all = false;
    this.exists_positions_select_all = false;

    if (!this.exists_positions.value.size && this.exists_next_page) {
      await this.loadExistsPositions(this.exists_current_page + 1);

      this.positions_key_exists++;
    }

    this.$emit("change", {
      add: this.ids_to_add.value,
      del: this.ids_to_del.value
    });
  }

  public checkExistsPosition(position: IAdmissionPositionOption) {
    this.$set(position, "is_check", !position.is_check);

    this.exists_positions_checked_count += position.is_check ? 1 : -1;
    this.exists_positions_select_all =
      this.exists_positions_checked_count === this.exists_positions.value.size;
  }
}
